import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Switch } from '../../components/ui/switch';
import { Plus, Search, Edit2, Trash2, Eye, ShieldCheck, Plus as PlusIcon, X, RefreshCw, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDate } from '../../lib/api';

const ROLE_COLORS = {
  Owner: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  'Co-owner': 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  'Manager chatter': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Chatter: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

// Sécurise un employé venu de l'API : garantit name/role/handle/accounts non-nuls.
// Sans ça, un seul champ manquant (ou une réponse partielle du serveur) faisait
// planter TOUTE la page vers l'écran « Souci d'affichage ».
const normChatter = (c) => ({
  ...c,
  name: c?.name || 'Sans nom',
  role: c?.role || 'Chatter',
  handle: c?.handle || '',
  accounts: Array.isArray(c?.accounts) ? c.accounts : [],
});

export default function Chatters() {
  const [team, setTeam] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [salesMap, setSalesMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    try {
      const [{ data: chatters }, { data: accs }] = await Promise.all([api.get('/chatters'), api.get('/accounts')]);
      const safe = (chatters || []).map(normChatter);
      setTeam(safe);
      setAccounts(accs || []);
      setSelected(prev => prev || safe[0]?.id);
    } catch (e) {
      toast.error(apiError(e, "Impossible de charger l'équipe"));
    }
    // Assignable roles = system roles (minus Owner) + any custom role.
    api.get('/roles').then(({ data }) => {
      const names = data.map(r => r.name).filter(n => n !== 'Owner');
      setRoles(names.length ? names : ['Chatter', 'Manager chatter', 'Co-owner']);
    }).catch(() => setRoles(['Chatter', 'Manager chatter', 'Co-owner']));
    api.get('/reports/employee').then(({ data }) => {
      const m = {};
      data.forEach(r => { m[r.chatter_id] = r.total_sales; });
      setSalesMap(m);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const updateChatter = async (id, patch) => {
    try {
      const { data } = await api.patch(`/chatters/${id}`, patch);
      // La réponse peut être partielle (ex. proxy/démo) → on fusionne sur l'existant
      // puis on re-sécurise, pour ne jamais perdre name/role/accounts à l'affichage.
      setTeam(team.map(x => x.id === id ? normChatter({ ...x, ...data, id }) : x));
      return true;
    } catch (e) {
      toast.error(apiError(e, 'Échec de la mise à jour'));
      return false;
    }
  };

  const removeChatter = async (id) => {
    try {
      await api.delete(`/chatters/${id}`);
      const next = team.filter(x => x.id !== id);
      setTeam(next);
      setSelected(next[0]?.id || null);
      toast.success('Chatter supprimé');
    } catch (e) {
      toast.error(apiError(e, 'Échec de la suppression'));
    }
  };

  const filtered = team.filter(t => t.name.toLowerCase().includes(q.toLowerCase()) || (t.handle || '').toLowerCase().includes(q.toLowerCase()));
  const detail = team.find(t => t.id === selected);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="chatters-page">
      <PageHeader
        title="Employés"
        subtitle={`${team.length} employés`}
        actions={<NewChatter onCreated={load} roles={roles} />}
      />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-4 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden flex flex-col max-h-[70vh]">
          <div className="p-3 border-b border-white/5">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="chatter-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un chatter..." className="bg-white/5 border-white/10 text-white pl-9 h-9" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-white/5">
            {filtered.map(t => (
              <button key={t.id} data-testid={`chatter-row-${t.id}`} onClick={() => setSelected(t.id)} className={`w-full text-left px-4 py-3 flex items-center gap-3 ${selected === t.id ? 'bg-white/[0.04] border-l-2 border-l-indigo-500' : 'border-l-2 border-l-transparent hover:bg-white/[0.02]'}`}>
                <div className="relative">
                  <Avatar className="w-9 h-9"><AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-xs font-semibold">{t.name.split(' ').map(x => x[0]).join('')}</AvatarFallback></Avatar>
                  {t.online && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#0A0A0C]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{t.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{t.handle} · {t.accounts.length} comptes</div>
                </div>
                <Badge variant="outline" className={`${ROLE_COLORS[t.role] || ROLE_COLORS.Chatter} text-[10px]`}>{t.role.split(' ')[0]}</Badge>
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8">
          {detail && <Detail t={detail} accounts={accounts} roles={roles} sales={salesMap[detail.id] || 0} onUpdate={updateChatter} onDelete={() => removeChatter(detail.id)} />}
        </div>
      </div>
    </div>
  );
}

function Detail({ t, accounts, roles, sales, onUpdate, onDelete }) {
  const roleOptions = (roles && roles.length ? roles : ['Chatter', 'Manager chatter', 'Co-owner']);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-5" data-testid="chatter-detail">
      <div className="flex items-center gap-4 pb-5 border-b border-white/5">
        <Avatar className="w-14 h-14"><AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-500 text-white font-bold text-lg">{t.name.split(' ').map(x => x[0]).join('')}</AvatarFallback></Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-white">{t.name}</h2>
            <Badge variant="outline" className={`${ROLE_COLORS[t.role] || ROLE_COLORS.Chatter}`}><ShieldCheck size={11} className="mr-1" /> {t.role}</Badge>
          </div>
          <div className="text-sm text-slate-400">{t.handle} · Créé le {fmtDate(t.joined)}</div>
        </div>
        {!String(t.id).startsWith('owner-') && (
        <Button data-testid={`delete-chatter-${t.id}`} variant="ghost" size="icon" className="text-rose-400 hover:bg-rose-500/10" onClick={onDelete}><Trash2 size={15} /></Button>
        )}
      </div>

      {String(t.id).startsWith('owner-') ? (
        // Owner / Co-owner have full access to every account by design — no
        // assignment needed (and no chatter record exists to assign onto).
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-300 flex items-center gap-2">
          <ShieldCheck size={15} /> Accès complet à tous les comptes — les Owners ne sont jamais restreints.
        </div>
      ) : (
      <div>
        <Label className="text-slate-300 text-xs mb-2 block">Rôle</Label>
        <select data-testid="chatter-role-select" value={t.role} onChange={async e => { const ok = await onUpdate(t.id, { role: e.target.value }); if (ok) toast.success('Rôle mis à jour'); }} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none focus:border-indigo-500/50 max-w-xs">
          {roleOptions.map(r => <option key={r} className="bg-[#0B0B0E]">{r}</option>)}
        </select>
      </div>
      )}

      {!String(t.id).startsWith('owner-') && (<>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Accès aux comptes ({t.accounts.length} assignés)</h3>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-4 py-2.5 font-semibold">Compte</th>
                <th className="px-4 py-2.5 font-semibold">Groupes</th>
                <th className="px-4 py-2.5 font-semibold">Expire</th>
                <th className="px-4 py-2.5 font-semibold">Statut</th>
                <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {t.accounts.map(aid => {
                const a = accounts.find(x => x.id === aid);
                if (!a) return null;
                return (
                  <tr key={aid}>
                    <td className="px-4 py-2.5 text-sm text-white"><div className={`inline-block w-5 h-5 rounded mr-2 bg-gradient-to-br ${a.color}`} />{a.name}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-400">Tous les groupes</td>
                    <td className="px-4 py-2.5"><Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400 text-[10px]">Jamais</Badge></td>
                    <td className="px-4 py-2.5"><Switch defaultChecked /></td>
                    <td className="px-4 py-2.5 text-right"><Button data-testid={`remove-access-${aid}`} variant="ghost" size="icon" className="h-6 w-6 text-rose-400 hover:bg-rose-500/10" onClick={async () => { const ok = await onUpdate(t.id, { accounts: t.accounts.filter(x => x !== aid) }); if (ok) toast.success('Accès retiré'); }}><X size={12} /></Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-white mb-2">Assigner un compte</h3>
        <div className="flex flex-wrap gap-2">
          {accounts.filter(a => !t.accounts.includes(a.id)).map(a => (
            <Button key={a.id} data-testid={`assign-account-${a.id}`} variant="outline" size="sm" className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 h-8 text-xs" onClick={async () => { const ok = await onUpdate(t.id, { accounts: [...t.accounts, a.id] }); if (ok) toast.success('Accès accordé'); }}>
              <PlusIcon size={11} className="mr-1" /> {a.name}
            </Button>
          ))}
        </div>
      </div>
      </>)}

      <div>
        <h3 className="text-sm font-semibold text-white mb-2">Ventes totales (approuvées)</h3>
        <div className="text-2xl font-bold text-emerald-400 font-display" data-testid="chatter-total-sales">{sales.toLocaleString()} €</div>
      </div>
    </div>
  );
}

function NewChatter({ onCreated, roles }) {
  const roleOptions = (roles && roles.length ? roles : ['Chatter', 'Manager chatter']);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', password: '', role: 'Chatter' });
  const [saving, setSaving] = useState(false);
  const [showCreds, setShowCreds] = useState(null);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let p = '';
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setForm(f => ({ ...f, password: p }));
  };

  const save = async () => {
    if (!form.name.trim() || !form.username.trim() || !form.password.trim()) {
      toast.error('Nom, identifiant et mot de passe sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      await api.post('/chatters', {
        name: form.name.trim(),
        username: form.username.trim().toLowerCase(),
        password: form.password,
        handle: form.username.trim().toLowerCase(),
        role: form.role,
      });
      setShowCreds({ username: form.username.trim().toLowerCase(), password: form.password });
      setForm({ name: '', username: '', password: '', role: 'Chatter' });
      toast.success('Chatter ajouté — partage les identifiants');
      onCreated();
    } catch (e) {
      toast.error(apiError(e, "Impossible d'ajouter le chatter"));
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = () => {
    if (!showCreds) return;
    const text = `Username: ${showCreds.username}\nPassword: ${showCreds.password}`;
    navigator.clipboard.writeText(text);
    toast.success('Identifiants copiés');
  };

  const closeAll = () => { setOpen(false); setShowCreds(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); else setOpen(true); }}>
      <DialogTrigger asChild><Button data-testid="add-chatter-button" className="bg-indigo-500 hover:bg-indigo-600"><Plus size={16} className="mr-1.5" /> Ajouter</Button></DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">{showCreds ? 'Chatter créé' : 'Ajouter un chatter'}</DialogTitle></DialogHeader>
        {showCreds ? (
          <div className="space-y-3 pt-2" data-testid="chatter-credentials">
            <p className="text-sm text-slate-400">Partage ces identifiants avec le chatter. Ils ne seront plus affichés.</p>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-2 font-mono text-sm">
              <div><span className="text-slate-500">Identifiant :</span> <span className="text-white" data-testid="creds-username">{showCreds.username}</span></div>
              <div><span className="text-slate-500">Mot de passe :</span> <span className="text-white" data-testid="creds-password">{showCreds.password}</span></div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="copy-creds-button" variant="outline" className="flex-1 border-white/10 bg-white/5 text-slate-200 hover:bg-white/10" onClick={copyCreds}><Copy size={14} className="mr-1.5" /> Copier</Button>
              <Button className="flex-1 bg-indigo-500 hover:bg-indigo-600" onClick={closeAll}>Terminé</Button>
            </div>
          </div>
        ) : (
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Nom complet</Label><Input data-testid="chatter-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" /></div>
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Identifiant</Label><Input data-testid="chatter-username-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value.replace(/\s+/g, '').toLowerCase() })} className="bg-white/5 border-white/10 text-white h-10" placeholder="ex. johndoe" /></div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Mot de passe</Label>
            <div className="flex gap-2">
              <Input data-testid="chatter-password-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-white/5 border-white/10 text-white h-10 font-mono flex-1" placeholder="Génère ou saisis" />
              <Button data-testid="generate-password-button" type="button" variant="outline" className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 h-10" onClick={generatePassword}><RefreshCw size={14} className="mr-1.5" /> Générer</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Rôle</Label>
            <select data-testid="chatter-new-role-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none">
              {roleOptions.map(r => <option key={r} className="bg-[#0B0B0E]">{r}</option>)}
            </select>
          </div>
          <Button data-testid="chatter-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Ajout...' : 'Ajouter'}</Button>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
